// Public website-lead capture → DataCentral CMS. Used by the campaign register
// flow (utm_campaign=waf_free) to record a sales lead BEFORE the account is
// created, so the lead survives even if the visitor abandons the password step.
//
//   POST /api/v4/cms/submit  (public, no auth) → contact_sources table
//
// The team requires the "Additional Tracking Information" block on the lead:
// referrer / host / current URL / submitted-at / user agent. The referrer is
// captured at LANDING (attribution.referer_url) — the SPA navigates away and
// clears document.referrer by submit time, so we must not read it live here.

import { dataCentralPublic } from '@/api/client';
import { getAttribution } from '@/lib/attribution';
import { getServiceInterestGroup } from '@/lib/lead-service-interest';

export interface WebsiteLead {
  firstname?: string;
  lastname?: string;
  /** Work email — the only backend-required field. */
  email: string;
  phone?: string;
  company?: string;
  /** Job position (Chức vụ). */
  position?: string;
  /** Company size (Quy mô doanh nghiệp). */
  companySize?: string;
  website?: string;
  /** Interested solutions (Mục đích sử dụng) — multi-select. */
  interestedSolutions?: string[];
  specificNeeds?: string;
}

/** Compact utm string for the lead's `tracking` field (stored in metadata). */
function trackingString(): string | undefined {
  const a = getAttribution();
  if (!a) return undefined;
  const parts: Record<string, string> = {};
  if (a.utm_source) parts.utm_source = a.utm_source;
  if (a.utm_medium) parts.utm_medium = a.utm_medium;
  if (a.utm_campaign) parts.utm_campaign = a.utm_campaign;
  if (a.promo_code) parts.promo_code = a.promo_code;
  const qs = new URLSearchParams(parts).toString();
  return qs || undefined;
}

/** Submit a website lead to DataCentral, attaching campaign + tracking context. */
export async function submitWebsiteLead(lead: WebsiteLead): Promise<void> {
  const a = getAttribution();
  // The REAL referrer — where the visitor came from — captured at landing
  // (document.referrer is cleared once the SPA navigates). Cross-origin referrers
  // are trimmed to the origin by the browser's referrer-policy (e.g. Google →
  // "https://www.google.com/"), and ad/social clicks often send none.
  const referrer = a?.referer_url || (typeof document !== 'undefined' ? document.referrer : '') || '';
  // When the caller didn't specify an interested service (e.g. the generic
  // signup form sends email only), infer it from the service page the visitor
  // came through so the lead's "Giải pháp" isn't left blank.
  const inferredGroup = getServiceInterestGroup();
  const interestedSolutions =
    lead.interestedSolutions?.length ? lead.interestedSolutions
    : inferredGroup ? [inferredGroup]
    : undefined;
  await dataCentralPublic.post('/cms/submit', {
    ...lead,
    interestedSolutions,
    source_website: window.location.host,
    // Backend maps `tracking` → the email "Tracking Referrer" + subject, so send
    // the real referrer; fall back to the utm string on direct/typed visits (no
    // referrer) so the line still identifies the campaign.
    tracking: referrer || trackingString(),
    // Additional Tracking Information (required by the sales lead email).
    host: window.location.host,
    referer: referrer || undefined,
    currentUrl: a?.landing_url || window.location.href,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    submittedAt: new Date().toISOString(),
  });
}
